import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

// --- Types ---

type ContractType = 'rent' | 'sale' | 'reservation';
type ContractStatus =
  | 'draft'
  | 'documents_requested'
  | 'documents_received'
  | 'pending_signature'
  | 'signed'
  | 'active'
  | 'expired'
  | 'cancelled';

interface Contract {
  id: number;
  property_id: number;
  property_title: string;
  tenant_user_id: number;
  tenant_name: string | null;
  contract_type: ContractType;
  status: ContractStatus;
  monthly_amount: number;
  currency: string;
  start_date: string;
  end_date: string;
  created_at: string;
  sign_token?: string | null;
}

interface ContractsResponse {
  data: Contract[];
  meta?: {
    total: number;
    page: number;
    page_size: number;
  };
}

interface DocumentRequest {
  id: number;
  contract_id: number;
  document_type: string;
  label: string;
  status: 'pending' | 'uploaded' | 'approved' | 'rejected';
  file_url?: string | null;
  rejection_reason?: string | null;
}

interface DocumentRequestsResponse {
  data: DocumentRequest[];
}

interface GenerateSignLinkResponse {
  data: {
    sign_url: string;
    token: string;
  };
}

// --- Constants ---

const CONTRACT_TYPE_LABEL: Record<ContractType, string> = {
  rent: 'Alquiler',
  sale: 'Venta',
  reservation: 'Reserva',
};

const CONTRACT_TYPE_BADGE: Record<ContractType, string> = {
  rent: 'badge-info',
  sale: 'badge-secondary',
  reservation: 'badge-accent',
};

const STATUS_LABEL: Record<ContractStatus, string> = {
  draft: 'Borrador',
  documents_requested: 'Documentos solicitados',
  documents_received: 'Documentos recibidos',
  pending_signature: 'Pendiente firma',
  signed: 'Firmado',
  active: 'Activo',
  expired: 'Vencido',
  cancelled: 'Cancelado',
};

const STATUS_BADGE: Record<ContractStatus, string> = {
  draft: 'badge-ghost',
  documents_requested: 'badge-info',
  documents_received: 'badge-accent',
  pending_signature: 'badge-warning',
  signed: 'badge-primary',
  active: 'badge-success',
  expired: 'badge-error',
  cancelled: 'badge-neutral',
};

const ALL_STATUSES: ContractStatus[] = [
  'draft',
  'documents_requested',
  'documents_received',
  'pending_signature',
  'signed',
  'active',
  'expired',
  'cancelled',
];

const DOC_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  uploaded: 'Subido',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

const DOC_STATUS_BADGE: Record<string, string> = {
  pending: 'badge-ghost',
  uploaded: 'badge-warning',
  approved: 'badge-success',
  rejected: 'badge-error',
};

const PREDEFINED_DOC_TYPES = [
  { value: 'recibo_sueldo', label: 'Recibo de sueldo' },
  { value: 'dni_frente_dorso', label: 'DNI frente y dorso' },
  { value: 'garantia_propietaria', label: 'Garantia propietaria' },
  { value: 'informe_dominio', label: 'Informe de dominio' },
  { value: 'otro', label: 'Otro' },
];

// --- Sub-components ---

function DocumentRequestModal({
  contractId,
  isOpen,
  onClose,
}: {
  contractId: number;
  isOpen: boolean;
  onClose: () => void;
}): React.ReactElement | null {
  const queryClient = useQueryClient();
  const [docItems, setDocItems] = useState<Array<{ type: string; customLabel: string }>>([
    { type: 'recibo_sueldo', customLabel: '' },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addDocItem = (): void => {
    setDocItems((prev) => [...prev, { type: 'recibo_sueldo', customLabel: '' }]);
  };

  const removeDocItem = (index: number): void => {
    setDocItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDocItem = (index: number, field: 'type' | 'customLabel', value: string): void => {
    setDocItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleSubmit = async (): Promise<void> => {
    if (docItems.length === 0) return;

    setIsSubmitting(true);
    try {
      const documents = docItems.map((item) => ({
        document_type: item.type,
        label:
          item.type === 'otro' && item.customLabel.trim()
            ? item.customLabel.trim()
            : PREDEFINED_DOC_TYPES.find((d) => d.value === item.type)?.label || item.type,
      }));

      await api.post(`/v1/contracts/${contractId}/documents/request`, { documents });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      onClose();
      setDocItems([{ type: 'recibo_sueldo', customLabel: '' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-w-lg">
        <h3 className="font-bold text-lg mb-4">Solicitar documentos</h3>

        <div className="space-y-3">
          {docItems.map((item, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="flex-1 space-y-2">
                <select
                  className="select select-bordered select-sm w-full"
                  value={item.type}
                  onChange={(e) => updateDocItem(index, 'type', e.target.value)}
                >
                  {PREDEFINED_DOC_TYPES.map((dt) => (
                    <option key={dt.value} value={dt.value}>
                      {dt.label}
                    </option>
                  ))}
                </select>
                {item.type === 'otro' && (
                  <input
                    type="text"
                    className="input input-bordered input-sm w-full"
                    placeholder="Nombre del documento"
                    value={item.customLabel}
                    onChange={(e) => updateDocItem(index, 'customLabel', e.target.value)}
                  />
                )}
              </div>
              {docItems.length > 1 && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-square text-error"
                  onClick={() => removeDocItem(index)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>

        <button type="button" className="btn btn-ghost btn-sm mt-3" onClick={addDocItem}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Agregar documento
        </button>

        <div className="modal-action">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting || docItems.length === 0}
          >
            {isSubmitting ? <span className="loading loading-spinner loading-sm" /> : 'Solicitar'}
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onClose}>close</button>
      </form>
    </dialog>
  );
}

function DocumentsReviewModal({
  contractId,
  isOpen,
  onClose,
}: {
  contractId: number;
  isOpen: boolean;
  onClose: () => void;
}): React.ReactElement | null {
  const queryClient = useQueryClient();
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const { data: docsResponse, isLoading } = useQuery({
    queryKey: ['contract-documents', contractId],
    queryFn: () => api.get<DocumentRequestsResponse>(`/v1/contracts/${contractId}/documents`),
    enabled: isOpen,
  });

  const documents = docsResponse?.data ?? [];

  const approveMutation = useMutation({
    mutationFn: (docId: number) =>
      api.post(`/v1/contracts/${contractId}/documents/${docId}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-documents', contractId] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ docId, reason }: { docId: number; reason: string }) =>
      api.post(`/v1/contracts/${contractId}/documents/${docId}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-documents', contractId] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      setRejectingId(null);
      setRejectionReason('');
    },
  });

  const handleReject = (docId: number): void => {
    if (!rejectionReason.trim()) return;
    rejectMutation.mutate({ docId, reason: rejectionReason.trim() });
  };

  if (!isOpen) return null;

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <h3 className="font-bold text-lg mb-4">Documentos del contrato</h3>

        {isLoading ? (
          <div className="flex justify-center p-8">
            <span className="loading loading-spinner loading-lg" />
          </div>
        ) : documents.length === 0 ? (
          <p className="text-base-content/60 text-center py-8">
            No hay documentos solicitados para este contrato.
          </p>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.id} className="border border-base-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{doc.label}</span>
                      <span className={`badge badge-xs ${DOC_STATUS_BADGE[doc.status]}`}>
                        {DOC_STATUS_LABEL[doc.status]}
                      </span>
                    </div>
                    <p className="text-xs text-base-content/50 mt-1">
                      Tipo: {PREDEFINED_DOC_TYPES.find((d) => d.value === doc.document_type)?.label || doc.document_type}
                    </p>
                    {doc.rejection_reason && (
                      <p className="text-xs text-error mt-1">
                        Motivo rechazo: {doc.rejection_reason}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {/* View uploaded file */}
                    {doc.file_url && (
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost btn-xs"
                        title="Ver archivo"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                      </a>
                    )}

                    {/* Approve / Reject buttons only for uploaded docs */}
                    {doc.status === 'uploaded' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-success btn-xs"
                          onClick={() => approveMutation.mutate(doc.id)}
                          disabled={approveMutation.isPending}
                          title="Aprobar"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="btn btn-error btn-xs"
                          onClick={() => setRejectingId(rejectingId === doc.id ? null : doc.id)}
                          title="Rechazar"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Rejection reason input */}
                {rejectingId === doc.id && (
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      className="input input-bordered input-sm flex-1"
                      placeholder="Motivo del rechazo"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-error btn-sm"
                      onClick={() => handleReject(doc.id)}
                      disabled={rejectMutation.isPending || !rejectionReason.trim()}
                    >
                      {rejectMutation.isPending ? (
                        <span className="loading loading-spinner loading-xs" />
                      ) : (
                        'Rechazar'
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setRejectingId(null);
                        setRejectionReason('');
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="modal-action">
          <button type="button" className="btn" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onClose}>close</button>
      </form>
    </dialog>
  );
}

// --- Main Component ---

export default function ContractsPage(): React.ReactElement {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'company_admin';

  const [filterStatus, setFilterStatus] = useState<string>('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  // Modal state
  const [docRequestModalContractId, setDocRequestModalContractId] = useState<number | null>(null);
  const [docReviewModalContractId, setDocReviewModalContractId] = useState<number | null>(null);
  const [copiedContractId, setCopiedContractId] = useState<number | null>(null);

  const queryParams: Record<string, string | number> = {
    page: paginationModel.page + 1,
    page_size: paginationModel.pageSize,
  };
  if (filterStatus) queryParams.status = filterStatus;

  const { data: response, isLoading } = useQuery({
    queryKey: ['contracts', queryParams],
    queryFn: () => api.get<ContractsResponse>('/v1/contracts', queryParams),
  });

  const contracts = response?.data ?? [];
  const totalRows = response?.meta?.total ?? 0;
  const totalPages = Math.ceil(totalRows / paginationModel.pageSize);

  // Generate sign link mutation
  const generateSignLinkMutation = useMutation({
    mutationFn: (contractId: number) =>
      api.post<GenerateSignLinkResponse>(`/v1/contracts/${contractId}/generate-sign-link`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });

  // Activate contract mutation
  const activateContractMutation = useMutation({
    mutationFn: (contractId: number) =>
      api.post(`/v1/contracts/${contractId}/activate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });

  const formatPrice = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSignUrl = useCallback((contract: Contract): string => {
    if (contract.sign_token) {
      return `${window.location.origin}/sign/${contract.sign_token}`;
    }
    return '';
  }, []);

  const handleCopySignLink = useCallback(
    async (contract: Contract): Promise<void> => {
      const url = getSignUrl(contract);
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        setCopiedContractId(contract.id);
        setTimeout(() => setCopiedContractId(null), 2000);
      } catch {
        // Fallback
        const textArea = document.createElement('textarea');
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopiedContractId(contract.id);
        setTimeout(() => setCopiedContractId(null), 2000);
      }
    },
    [getSignUrl]
  );

  const handleGenerateSignLink = useCallback(
    (contractId: number): void => {
      generateSignLinkMutation.mutate(contractId);
    },
    [generateSignLinkMutation]
  );

  const handleActivateContract = useCallback(
    (contractId: number): void => {
      activateContractMutation.mutate(contractId);
    },
    [activateContractMutation]
  );

  // Reset page when filter changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filterStatus]);

  // --- Actions dropdown for a contract ---
  const renderActionsDropdown = (contract: Contract): React.ReactElement => {
    return (
      <div className="dropdown dropdown-end">
        <label tabIndex={0} className="btn btn-ghost btn-xs">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
          </svg>
        </label>
        <ul tabIndex={0} className="dropdown-content z-[1] menu menu-sm p-2 shadow-lg bg-base-100 rounded-box w-56">
          {/* Always: View */}
          <li>
            <RouterLink to={`/app/contracts/${contract.id}/sign`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              Ver contrato
            </RouterLink>
          </li>

          {/* Draft: Edit */}
          {contract.status === 'draft' && (
            <li>
              <RouterLink to={`/app/contracts/${contract.id}/edit`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Editar contrato
              </RouterLink>
            </li>
          )}

          {/* Draft: Request documents */}
          {contract.status === 'draft' && (
            <li>
              <button onClick={() => setDocRequestModalContractId(contract.id)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
                Solicitar documentos
              </button>
            </li>
          )}

          {/* Documents requested/received: Review documents */}
          {(contract.status === 'documents_requested' || contract.status === 'documents_received') && (
            <li>
              <button onClick={() => setDocReviewModalContractId(contract.id)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Revisar documentos
              </button>
            </li>
          )}

          {/* Documents received: Generate sign link */}
          {contract.status === 'documents_received' && (
            <li>
              <button
                onClick={() => handleGenerateSignLink(contract.id)}
                disabled={generateSignLinkMutation.isPending}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                </svg>
                {generateSignLinkMutation.isPending ? 'Generando...' : 'Generar link de firma'}
              </button>
            </li>
          )}

          {/* Pending signature: Copy sign link */}
          {contract.status === 'pending_signature' && contract.sign_token && (
            <li>
              <button onClick={() => handleCopySignLink(contract)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z" />
                  <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5z" />
                </svg>
                {copiedContractId === contract.id ? 'Copiado!' : 'Copiar link de firma'}
              </button>
            </li>
          )}

          {/* Pending signature: Internal sign */}
          {contract.status === 'pending_signature' && (
            <li>
              <RouterLink to={`/app/contracts/${contract.id}/sign`} className="text-success">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Firmar contrato
              </RouterLink>
            </li>
          )}

          {/* Signed: Activate */}
          {contract.status === 'signed' && (
            <li>
              <button
                onClick={() => handleActivateContract(contract.id)}
                className="text-success"
                disabled={activateContractMutation.isPending}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {activateContractMutation.isPending ? 'Activando...' : 'Activar contrato'}
              </button>
            </li>
          )}
        </ul>
      </div>
    );
  };

  // --- Admin Table View ---
  if (isAdmin) {
    return (
      <div>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Contratos</h1>
            <p className="text-sm text-base-content/60">Gestion de contratos de tu inmobiliaria</p>
          </div>
          <RouterLink to="/app/contracts/new" className="btn btn-primary">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Nuevo contrato
          </RouterLink>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <select
            className="select select-bordered select-sm w-56"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">Estado - Todos</option>
            {ALL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABEL[status]}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-base-100 rounded-lg">
          {isLoading ? (
            <div className="flex justify-center p-8">
              <span className="loading loading-spinner loading-lg" />
            </div>
          ) : (
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Propiedad</th>
                  <th>Inquilino</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Monto</th>
                  <th>Vencimiento</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {contracts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-base-content/60 py-8">
                      No hay contratos registrados
                    </td>
                  </tr>
                ) : (
                  contracts.map((contract) => (
                    <tr key={contract.id}>
                      <td className="font-medium max-w-xs truncate">{contract.property_title}</td>
                      <td>{contract.tenant_name || '-'}</td>
                      <td>
                        <span className={`badge badge-sm ${CONTRACT_TYPE_BADGE[contract.contract_type]}`}>
                          {CONTRACT_TYPE_LABEL[contract.contract_type]}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-sm ${STATUS_BADGE[contract.status]}`}>
                          {STATUS_LABEL[contract.status] || contract.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap">
                        {formatPrice(contract.monthly_amount, contract.currency)}
                      </td>
                      <td className="whitespace-nowrap">{formatDate(contract.end_date)}</td>
                      <td>
                        {renderActionsDropdown(contract)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {totalRows > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-base-200">
              <span className="text-sm text-base-content/60">
                Pagina {paginationModel.page + 1} de {totalPages}
              </span>
              <div className="join">
                <button
                  className="join-item btn btn-sm"
                  disabled={paginationModel.page === 0}
                  onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  &laquo;
                </button>
                <button className="join-item btn btn-sm">
                  {paginationModel.page + 1}
                </button>
                <button
                  className="join-item btn btn-sm"
                  disabled={paginationModel.page >= totalPages - 1}
                  onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  &raquo;
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Document Request Modal */}
        {docRequestModalContractId !== null && (
          <DocumentRequestModal
            contractId={docRequestModalContractId}
            isOpen={true}
            onClose={() => setDocRequestModalContractId(null)}
          />
        )}

        {/* Documents Review Modal */}
        {docReviewModalContractId !== null && (
          <DocumentsReviewModal
            contractId={docReviewModalContractId}
            isOpen={true}
            onClose={() => setDocReviewModalContractId(null)}
          />
        )}
      </div>
    );
  }

  // --- End User Card View ---
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Mis Contratos</h1>
        <p className="text-sm text-base-content/60">Tus contratos vigentes y anteriores</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : contracts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-4xl mb-4">&#128196;</p>
          <p className="text-lg text-base-content/60">No tenes contratos asignados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contracts.map((contract) => (
            <div key={contract.id} className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold">{contract.property_title}</h3>
                    <p className="text-sm text-base-content/60 mt-1">
                      <span className={`badge badge-xs ${CONTRACT_TYPE_BADGE[contract.contract_type]} mr-2`}>
                        {CONTRACT_TYPE_LABEL[contract.contract_type]}
                      </span>
                      {formatPrice(contract.monthly_amount, contract.currency)}/mes
                    </p>
                    <p className="text-sm text-base-content/60">
                      Desde {formatDate(contract.start_date)} hasta {formatDate(contract.end_date)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`badge badge-sm ${STATUS_BADGE[contract.status]}`}>
                      {STATUS_LABEL[contract.status] || contract.status}
                    </span>
                    {contract.status === 'pending_signature' && (
                      <RouterLink
                        to={`/app/contracts/${contract.id}/sign`}
                        className="btn btn-success btn-xs"
                      >
                        Firmar
                      </RouterLink>
                    )}
                    {contract.status !== 'pending_signature' && (
                      <RouterLink
                        to={`/app/contracts/${contract.id}/sign`}
                        className="btn btn-ghost btn-xs"
                      >
                        Ver detalle
                      </RouterLink>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
